import { type MouseEventHandler, useCallback, useEffect } from 'react';
import { atom, selectorFamily, useRecoilValue, useSetRecoilState } from 'recoil';

import { useEventCallback, useLiveRef } from '@gitbook/util-react-hooks';

const hoveredBlocksState = atom<string[]>({
    key: 'hoveredBlocks',
    default: [],
});

const isLastHoveredSelector = selectorFamily({
    key: 'hoveredBlocks-isLastHovered',
    get:
        (blockKey: string) =>
        ({ get }) => {
            const hoveredBlocks = get(hoveredBlocksState);
            return hoveredBlocks[0] === blockKey;
        },
});

const isHoveredSelector = selectorFamily({
    key: 'hoveredBlocks-isHovered',
    get:
        (nodeKey: string) =>
        ({ get }) => {
            const hoveredBlocks = get(hoveredBlocksState);
            return hoveredBlocks.some((block) => block === nodeKey);
        },
});

/**
 * Set the hovered blocks.
 */
function useSetHoveredBlocks() {
    return useSetRecoilState(hoveredBlocksState);
}

/**
 * Hook to handle block hover.
 */
export function useBlockHover(props: {
    nodeKey: string;
    onBlockHover?: (nodeKey: string | null) => void;
}) {
    const { nodeKey } = props;

    const onBlockHoverRef = useLiveRef(props.onBlockHover);
    const setHoveredBlocks = useSetHoveredBlocks();

    const markBlockAsHovered = useCallback(() => {
        setHoveredBlocks((prev) => {
            return Array.from(new Set([nodeKey, ...prev])); // make sure values are unique
        });
        onBlockHoverRef.current?.(nodeKey);
    }, [nodeKey, setHoveredBlocks, onBlockHoverRef]);

    const markBlockAsUnhovered = useCallback(() => {
        setHoveredBlocks((prev) => {
            return prev.filter((block) => block !== nodeKey);
        });
        onBlockHoverRef.current?.(null);
    }, [nodeKey, setHoveredBlocks, onBlockHoverRef]);

    // Remove the block from the hovered blocks when it unmounts.
    useEffect(() => () => markBlockAsUnhovered(), [markBlockAsUnhovered]);

    const onMouseEnter: MouseEventHandler = useEventCallback(() => {
        markBlockAsHovered();
    });

    const onMouseLeave: MouseEventHandler = useEventCallback(() => {
        markBlockAsUnhovered();
    });

    return {
        containerProps: {
            onMouseEnter,
            onMouseLeave,
        },
    };
}

/**
 * Check if a block is the last one hovered.
 */
export function useIsLastBlockHovered(nodeKey: string) {
    return useRecoilValue(isLastHoveredSelector(nodeKey));
}

/**
 * Check if a block is hovered.
 */
export function useIsBlockHovered(nodeKey: string) {
    return useRecoilValue(isHoveredSelector(nodeKey));
}
